
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'

interface LeaderboardUser {
  id: string
  rank: number
  username: string
  displayName: string
  avatar: string
  points: number
  badge: string
  badgeColor: string
  trend: 'up' | 'down' | 'same'
  trendValue: number
  activityCount: number
  slug?: string
  isBot?: boolean
}

const LeaderboardWidget = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'weekly' | 'monthly' | 'allTime'>('weekly')
  const [leaderboardData, setLeaderboardData] = useState<Record<string, LeaderboardUser[]>>({
    weekly: [],
    monthly: [],
    allTime: []
  })
  const [loading, setLoading] = useState(true)

  const defaultAvatars = [
    'https://public.readdy.ai/ai/img_res/72916e8f12c864aeebdeb8af8dd219bb.jpg',
    'https://readdy.ai/api/search-image?query=Professional%20portrait%20photo%20of%20young%20Middle%20Eastern%20man%20friendly%20expression%20casual%20style%20soft%20studio%20lighting%20neutral%20background%20high%20quality%20headshot&width=80&height=80&seq=leader2&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Professional%20portrait%20photo%20of%20young%20Arab%20woman%20creative%20artist%20vibe%20stylish%20appearance%20soft%20studio%20lighting%20neutral%20background%20high%20quality%20headshot&width=80&height=80&seq=leader3&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Professional%20portrait%20photo%20of%20young%20Arab%20man%20tech%20enthusiast%20modern%20style%20glasses%20soft%20studio%20lighting%20neutral%20background%20high%20quality%20headshot&width=80&height=80&seq=leader4&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Professional%20portrait%20photo%20of%20young%20Arab%20woman%20creative%20professional%20confident%20smile%20soft%20studio%20lighting%20neutral%20background%20high%20quality%20headshot&width=80&height=80&seq=leader5&orientation=squarish'
  ]

  const getBadgeInfo = (points: number, rank: number): { badge: string; badgeColor: string } => {
    if (rank === 1) return { badge: 'Top Contributor', badgeColor: 'bg-amber-500' }
    if (rank === 2) return { badge: 'Rising Star', badgeColor: 'bg-teal-500' }
    if (rank === 3) return { badge: 'Active Member', badgeColor: 'bg-emerald-500' }
    if (points >= 1000) return { badge: 'Veteran', badgeColor: 'bg-violet-500' }
    if (points >= 500) return { badge: 'Contributor', badgeColor: 'bg-rose-500' }
    return { badge: 'Member', badgeColor: 'bg-gray-500' }
  }

  useEffect(() => {
    fetchLeaderboardData()
  }, [])

  const fetchLeaderboardData = async () => {
    setLoading(true)
    try {
      const now = new Date()
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

      // Fetch real users
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url, avatar_id, reputation_points')
        .eq('is_banned', false)
        .order('reputation_points', { ascending: false })
        .limit(20)

      if (usersError) throw usersError

      // Fetch bot users from bot_profiles
      const { data: botUsers, error: botError } = await supabase
        .from('bot_profiles')
        .select('id, username, slug, avatar_url, reputation, followers_count, is_bot')
        .eq('is_bot', true)
        .order('reputation', { ascending: false })
        .limit(20)

      if (botError) throw botError

      // Merge real users and bot users into a unified list
      const realUserEntries = (users || []).map((u, i) => ({
        id: u.id,
        username: u.username || `user_${u.id.slice(0, 8)}`,
        displayName: u.username || `User ${i + 1}`,
        avatar: u.avatar_url || defaultAvatars[i % defaultAvatars.length],
        basePoints: u.reputation_points || 0,
        isBot: false,
        slug: u.username
      }))

      const botUserEntries = (botUsers || []).map((b) => ({
        id: b.id,
        username: b.username,
        displayName: b.username,
        avatar: b.avatar_url || defaultAvatars[0],
        basePoints: b.reputation || 0,
        isBot: true,
        slug: b.slug || b.username
      }))

      const allUsers = [...realUserEntries, ...botUserEntries]

      if (allUsers.length === 0) {
        setLoading(false)
        return
      }

      // Fetch activity counts for real users only (bots get simulated activity)
      const realUserIds = realUserEntries.map(u => u.id)

      let weeklyActivities: { user_id: string }[] | null = null
      let monthlyActivities: { user_id: string }[] | null = null
      let allTimeActivities: { user_id: string }[] | null = null

      if (realUserIds.length > 0) {
        const { data: wa } = await supabase
          .from('user_activities')
          .select('user_id')
          .in('user_id', realUserIds)
          .gte('created_at', weekAgo.toISOString())
        weeklyActivities = wa

        const { data: ma } = await supabase
          .from('user_activities')
          .select('user_id')
          .in('user_id', realUserIds)
          .gte('created_at', monthAgo.toISOString())
        monthlyActivities = ma

        const { data: ata } = await supabase
          .from('user_activities')
          .select('user_id')
          .in('user_id', realUserIds)
        allTimeActivities = ata
      }

      const countActivities = (activities: { user_id: string }[] | null, userId: string) => {
        if (!activities) return 0
        return activities.filter(a => a.user_id === userId).length
      }

      // Simulate bot activity based on their reputation and followers
      const getBotActivityCount = (basePoints: number, period: 'weekly' | 'monthly' | 'allTime') => {
        const base = Math.floor(basePoints / 50)
        if (period === 'weekly') return Math.max(2, Math.floor(base * 0.3))
        if (period === 'monthly') return Math.max(5, Math.floor(base * 0.7))
        return Math.max(10, base)
      }

      const calculateUserData = (
        activities: { user_id: string }[] | null,
        period: 'weekly' | 'monthly' | 'allTime'
      ): LeaderboardUser[] => {
        const usersWithPoints = allUsers.map((user) => {
          const activityCount = user.isBot
            ? getBotActivityCount(user.basePoints, period)
            : countActivities(activities, user.id)
          const activityPoints = activityCount * 10
          const totalPoints = user.basePoints + activityPoints

          return {
            id: user.id,
            rank: 0,
            username: user.username,
            displayName: user.displayName,
            avatar: user.avatar,
            points: totalPoints,
            badge: '',
            badgeColor: '',
            trend: 'same' as const,
            trendValue: 0,
            activityCount,
            slug: user.slug,
            isBot: user.isBot
          }
        })

        usersWithPoints.sort((a, b) => b.points - a.points)

        return usersWithPoints.slice(0, 5).map((user, index) => {
          const rank = index + 1
          const badgeInfo = getBadgeInfo(user.points, rank)

          let trend: 'up' | 'down' | 'same' = 'same'
          let trendValue = 0
          if (user.activityCount > 5) {
            trend = 'up'
            trendValue = Math.min(user.activityCount, 5)
          } else if (user.activityCount === 0 && rank > 3) {
            trend = 'down'
            trendValue = 1
          }

          return {
            ...user,
            rank,
            badge: badgeInfo.badge,
            badgeColor: badgeInfo.badgeColor,
            trend,
            trendValue
          }
        })
      }

      setLeaderboardData({
        weekly: calculateUserData(weeklyActivities, 'weekly'),
        monthly: calculateUserData(monthlyActivities, 'monthly'),
        allTime: calculateUserData(allTimeActivities, 'allTime')
      })
    } catch (error) {
      console.error('Error fetching leaderboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const currentData = leaderboardData[activeTab]

  const formatPoints = (points: number): string => {
    if (points >= 1000) {
      return (points / 1000).toFixed(1) + 'K'
    }
    return points.toString()
  }

  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-amber-400 to-amber-500 text-white'
      case 2:
        return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-700'
      case 3:
        return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  const getTrendIcon = (trend: 'up' | 'down' | 'same') => {
    switch (trend) {
      case 'up':
        return <i className="ri-arrow-up-s-fill text-emerald-500"></i>
      case 'down':
        return <i className="ri-arrow-down-s-fill text-rose-500"></i>
      default:
        return <i className="ri-subtract-line text-gray-400"></i>
    }
  }

  const handleUserClick = (user: LeaderboardUser) => {
    if (user.isBot && user.slug) {
      navigate(`/user/${user.slug}`)
    } else {
      navigate(`/user/${user.username}`)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gradient-to-r from-amber-500 to-orange-500">
        <h3 className="text-sm font-semibold text-white flex items-center">
          <i className="ri-trophy-line mr-1.5"></i>
          Leaderboard
        </h3>
        <p className="text-amber-100 text-xs mt-0.5">Top community contributors</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-gray-50">
        {[
          { key: 'weekly', label: 'Week' },
          { key: 'monthly', label: 'Month' },
          { key: 'allTime', label: 'All Time' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as 'weekly' | 'monthly' | 'allTime')}
            className={`flex-1 py-2 text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'text-amber-600 border-b-2 border-amber-500 bg-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <i className="ri-loader-4-line animate-spin text-2xl text-amber-500 mb-2"></i>
            <span className="text-xs text-gray-500">Loading leaderboard...</span>
          </div>
        ) : currentData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <i className="ri-user-line text-3xl mb-2"></i>
            <span className="text-xs">No users yet</span>
            <span className="text-xs text-gray-400">Be the first to join!</span>
          </div>
        ) : (
          currentData.map((user) => (
            <div
              key={user.id}
              onClick={() => handleUserClick(user)}
              className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
            >
              {/* Rank */}
              <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${getRankStyle(user.rank)}`}>
                {user.rank}
              </div>

              {/* Avatar */}
              <div className="relative">
                <img
                  src={user.avatar}
                  alt={user.displayName}
                  className="w-9 h-9 rounded-full object-cover object-top border-2 border-white shadow-sm"
                />
                {user.rank <= 3 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center">
                    {user.rank === 1 && <i className="ri-vip-crown-fill text-amber-500 text-sm"></i>}
                    {user.rank === 2 && <i className="ri-medal-line text-gray-400 text-sm"></i>}
                    {user.rank === 3 && <i className="ri-medal-line text-amber-700 text-sm"></i>}
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-gray-800 truncate group-hover:text-amber-600 transition-colors">
                    {user.displayName}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full text-white ${user.badgeColor}`}>
                    {user.badge}
                  </span>
                </div>
                <div className="text-xs text-gray-500">@{user.isBot ? user.slug : user.username}</div>
              </div>

              {/* Points & Trend */}
              <div className="text-right">
                <div className="text-sm font-bold text-gray-800">{formatPoints(user.points)}</div>
                <div className="flex items-center justify-end text-xs">
                  {getTrendIcon(user.trend)}
                  {user.trendValue > 0 && (
                    <span className={user.trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}>
                      {user.trendValue}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* View All Link */}
      {/* Removed - View Full Leaderboard button */}
    </div>
  )
}

export default LeaderboardWidget
